import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { Filter, SessionCookieService } from '@docpath/linces-angular-components/common';
import { DpFieldText, DpFormButton, DpFormButtonType, DpFormModule, DpFormService, DpHeaderAction, DpHeaderModule, DpHeaderSwitch, DpSectionForm, Table } from '@docpath/webcomponents';
import { Subscription } from 'rxjs';

import { Constants } from '../../../constants/constants';
import { IndexServicesHeaderComponent } from './components/header/index-services-header.component';
import { IndexServicesTableComponent } from './components/table/index-services-table.component';
import { IndexServicesTableService } from './components/table/index-services-table.service';
import { IndexService } from './models/index-service';
import { AppService, ServicesConfig } from './models/services-config';
import { IndexServicesService } from './services/index-services.service';
import { ServicesFormService } from './services/services-form.service';
import { ServicesHttpService } from './services/services-http.service';


@Component({
  selector: 'app-services',
  templateUrl: './services.component.html',
  standalone: true,
  imports: [DpFormModule, DpHeaderModule, IndexServicesHeaderComponent, IndexServicesTableComponent]
})
export class ServicesComponent implements OnInit, OnDestroy {

  sectionForm: DpSectionForm;
  filters: Filter[] = [];
  selectedIndexServices: IndexService[] = [];

  isAdminUser = false;
  config?: ServicesConfig;
  headerPrefix = 'services-config.header';
  title = `${this.headerPrefix}.title`;
  rightActions: DpHeaderAction[] = [];
  automaticSetup = false;
  switch: DpHeaderSwitch;

  private table?: Table;
  private indexServicesChangedSubscription?: Subscription;

  private readonly sessionCookieService = inject(SessionCookieService);
  private readonly servicesHttpService = inject(ServicesHttpService);
  private readonly aimService = inject(ServicesFormService);
  private readonly indexServicesService = inject(IndexServicesService);
  private readonly indexServicesTableService = inject(IndexServicesTableService);
  private readonly formService = inject(DpFormService);

  constructor() {
    this.switch = this.initSwitch();
    this.sectionForm = this.aimService.getSectionForm();
    this.sectionForm.buttons = this.getFormButtons();
    const user = this.sessionCookieService.getSessionCookie()?.user;
    this.isAdminUser = user?.role === Constants.ROLE_ADMIN;

    this.sectionForm.formGroupAdded.subscribe(() => {
      this.onChangeUrls(this.sectionForm);
    });
  }

  ngOnInit(): void {
    this.servicesHttpService.getConfig().subscribe(config => {
      this.initServicesPage(config);
    });
    if (!this.indexServicesChangedSubscription) {
      this.indexServicesChangedSubscription = this.indexServicesService.indexServicesChanged.subscribe(() => this.refreshIndexServices());
    }
  }

  ngOnDestroy(): void {
    if (this.indexServicesChangedSubscription) {
      this.indexServicesChangedSubscription.unsubscribe();
    }
  }

  cancel() {
    const { originValue } = this.sectionForm;

    this.formService.checkChanges(this.sectionForm.getValue(), originValue).subscribe({
      complete: () => {
        this.sectionForm.reset();
        this.initAutomaticSetup(originValue.general?.automatic);
      }
    });
  }

  submitForm(value: unknown) {
    const updatedConfig = {} as ServicesConfig;

    [AppService.Aim].forEach(serviceKey => {
      const formValue = (value as Record<string, unknown>)[serviceKey],
        configValue = ((this.config ?? {}) as Record<string, unknown>)[serviceKey];

      if (formValue) {
        (updatedConfig as unknown as Record<string, unknown>)[serviceKey] = {
          ...(configValue ?? {}),
          ...formValue
        };
      }
    }
    );

    updatedConfig.automatic = this.automaticSetup;

    this.servicesHttpService.updateConfig(updatedConfig).subscribe({
      complete: () => {
        this.initServicesPage(updatedConfig);
      }
    });
  }

  private getFormButtons(): DpFormButton[] {
    return [
      new DpFormButton({
        type: DpFormButtonType.Cancel,
        onClick: () => this.cancel()
      }),
      new DpFormButton({ type: DpFormButtonType.Submit })
    ];
  }

  private initAutomaticSetup(automatic = false, updateFormValue = false, updateSwitchValue = true): void {
    this.automaticSetup = automatic;

    if (updateSwitchValue) {
      this.switch.checked = this.automaticSetup;
    }

    if (updateFormValue) {
      this.sectionForm.formGroup?.patchValue({ general: { automatic: this.automaticSetup } });
    }
  }


  private initServicesPage(config: ServicesConfig): void {
    this.config = config;
    this.initAutomaticSetup(config.automatic);
    this.sectionForm.setValue({ ...config, general: { automatic: this.automaticSetup } });
  }

  private initSwitch(initialValue = false): DpHeaderSwitch {
    return {
      label: `${this.headerPrefix}.switch.label`,
      tooltip: `${this.headerPrefix}.switch.tooltip`,
      checked: initialValue,
      onChange: (checked: boolean) => this.onChangeAutomatic(checked)
    } as DpHeaderSwitch;
  }

  private onChangeAutomatic(checked: boolean): void {
    this.initAutomaticSetup(checked, true, false);
  }

  private onChangeUrls(form: DpSectionForm): void {
    const tokenField = this.sectionForm.getField('token', AppService.Aim) as DpFieldText;

    form.formGroup?.get(`${AppService.Aim}.url`)?.valueChanges.subscribe(value => {
      if (tokenField) {
        tokenField.disabled = !this.automaticSetup && !value;
      }
    });
  }

  getTable(): Table | undefined {
    return this.table;
  }

  onTableLoaded(table: Table): void {
    this.table = table;
  }

  onFilterChange(filters: Filter[]): void {
    this.filters = filters;
  }

  onCheckChange(): void {
    if (this.table) {
      this.selectedIndexServices = this.indexServicesTableService.getSelectedIndexServices(this.table);
    }
  }

  getSelectedIndexServiceCount(): number {
    if (this.table) {
      const indexServices = this.indexServicesTableService.getSelectedIndexServices(this.table);

      return indexServices.length;
    }

    return 0;
  }

  getSelectedIndexService(): IndexService | undefined {
    if (this.table) {
      const indexServices = this.indexServicesTableService.getSelectedIndexServices(this.table);

      return indexServices.length === 1 ? indexServices[0] : undefined;
    }

    return undefined;
  }

  private refreshIndexServices(): void {
    this.indexServicesTableService.loadIndexServices(this.filters, 1);
  }

}